import { supabaseClient, useUser } from '@auth-v2';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
    SendMessagePayload,
    sendSwingThreadMessage,
    readSwingThreadMessages,
    deleteSwingThreadMessage,
    DeleteMessagePayload,
} from '@data';

import { queryClient } from '../config';

export const getSwingThreadMessagesKey = (threadId: number) => ['swingThread', threadId, 'messages'];

export function useSendMessage() {
    const { data: user } = useUser();
    return useMutation({
        mutationFn: (payload: Omit<SendMessagePayload, 'createdBy' | 'authorName'>) =>
            sendSwingThreadMessage(supabaseClient, {
                ...payload,
                createdBy: user!.id,
                authorName: user!.user_metadata?.['name'] ?? 'Unknown',
            }),
        onSuccess: (_, variables) => {
            if ('threadId' in variables && variables.threadId) {
                queryClient.invalidateQueries({
                    queryKey: getSwingThreadMessagesKey(variables.threadId),
                });
            }
        },
    });
}

export const useSwingThreadMessages = (threadId: number) => {
    return useQuery({
        queryKey: getSwingThreadMessagesKey(threadId),
        queryFn: () => readSwingThreadMessages(supabaseClient, threadId),
        enabled: threadId > 0,
        refetchOnMount: false,
    });
};

export const useDeleteThreadMessage = () => {
    return useMutation({
        mutationFn: (payload: DeleteMessagePayload) => deleteSwingThreadMessage(supabaseClient, payload),
        onSuccess: (_, variables) => {
            if ('threadId' in variables && variables.threadId) {
                queryClient.invalidateQueries({
                    queryKey: getSwingThreadMessagesKey(variables.threadId),
                });
            }
        },
    });
};
